"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SettingsFacade = exports.isProgressSetting = void 0;
const constants_1 = require("@treefort/constants");
const event_emitter_1 = require("./event-emitter");
const progress_persistence_1 = require("./progress-persistence");
const settings_1 = __importDefault(require("./settings"));
const settings_2 = require("./types/settings");
const progressKeyRegex = new RegExp(`^${constants_1.PROGRESS_KEY_PREFIX}\\.${constants_1.PROGRESS_VERSION}\\.\\w+`);
function isProgressSetting(key) {
    return progressKeyRegex.test(key);
}
exports.isProgressSetting = isProgressSetting;
class SettingsFacade extends event_emitter_1.EventEmitter {
    constructor({ store, api, localOnly = () => false, onRemoteError = (e) => {
        throw e;
    }, progressStore, }) {
        super();
        this.getLocalOrRemote = async (key, options) => {
            if (isProgressSetting(key)) {
                return await this.progressPersistence.getLocal(key, options);
            }
            return await this.legacySettings.getLocalOrRemote(key, options);
        };
        this.getRemote = async (key, options) => {
            if (isProgressSetting(key)) {
                // getLocal is not a typo. Watermelon persistence syncs in the background
                // and we do not need to explicitly go fetch remote.
                return await this.progressPersistence.getLocal(key, options);
            }
            return await this.legacySettings.getRemote(key, options);
        };
        this.clearLocal = async (key, options) => {
            await Promise.all(this.getPersistencesFor(key).map((persistence) => persistence.clearLocal(key, options)));
            this.emitter.emit(settings_2.Event.LocalSettingUpdated, { key, value: null });
        };
        this.clearRemote = async (key, options) => {
            await Promise.all(this.getPersistencesFor(key).map((persistence) => persistence.clearRemote(key, options)));
            this.emitter.emit(settings_2.Event.RemoteSettingUpdated, { key, value: null });
        };
        this.clearLocalAndRemote = async (key, options) => {
            await Promise.all(this.getPersistencesFor(key).map((persistence) => persistence.clearLocalAndRemote(key, options)));
            this.emitter.emit(settings_2.Event.LocalSettingUpdated, { key, value: null });
            this.emitter.emit(settings_2.Event.RemoteSettingUpdated, { key, value: null });
        };
        this.saveLocal = async (key, value, options) => {
            await Promise.all(this.getPersistencesFor(key).map((persistence) => persistence.saveLocal(key, value, options)));
            this.emitter.emit(settings_2.Event.LocalSettingUpdated, { key, value });
        };
        this.saveRemote = async (key, value, options) => {
            await Promise.all(this.getPersistencesFor(key).map((persistence) => persistence.saveRemote(key, value, options)));
            this.emitter.emit(settings_2.Event.RemoteSettingUpdated, { key, value });
        };
        this.updateRemote = async (key, callback, options) => {
            const updatedValue = await this.legacySettings.updateRemote(key, callback, options);
            this.emitter.emit(settings_2.Event.RemoteSettingUpdated, { key, value: updatedValue });
            return updatedValue;
        };
        this.saveLocalAndRemote = async (key, value, options) => {
            await Promise.all(this.getPersistencesFor(key).map((persistence) => persistence.saveLocalAndRemote(key, value, options)));
            this.emitter.emit(settings_2.Event.LocalSettingUpdated, { key, value });
            this.emitter.emit(settings_2.Event.RemoteSettingUpdated, { key, value });
        };
        this.getLocal = async (key, options) => {
            if (isProgressSetting(key)) {
                return await this.progressPersistence.getLocal(key, options);
            }
            return await this.legacySettings.getLocal(key, options);
        };
        this.getManyLocal = async (keys, options) => {
            return await this.legacySettings.getManyLocal(keys, options);
        };
        this.legacySettings = new settings_1.default({
            store,
            api,
            localOnly,
            onRemoteError,
        });
        this.progressPersistence = new progress_persistence_1.ProgressPersistence(progressStore);
        // We'll use whatever localOnly value is being provided for the legacy
        // settings because Watermelon-based settings don't care - calling code
        // shouldn't be making remote API calls independent of the sync.
        this.localOnly = localOnly;
    }
    getPersistencesFor(key) {
        return isProgressSetting(key)
            ? [this.legacySettings, this.progressPersistence]
            : [this.legacySettings];
    }
}
exports.SettingsFacade = SettingsFacade;
